import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useViewMode } from '@/contexts/ViewModeContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  BookOpen,
  MagnifyingGlass,
  PlayCircle,
  Shield,
  X
} from '@phosphor-icons/react';

/**
 * Welcome widget compacto post-login (bottom-right).
 *
 * Reemplaza el antiguo modal full-screen de 4 pasos. NO bloquea la UI ni
 * hace overlay. Solo informa y ofrece accesos: tour guiado, guía y cerrar.
 *
 * Persistencia: localStorage `vektor_welcome_dismissed`. Una vez cerrado,
 * no vuelve a aparecer salvo que se borre la flag.
 *
 * Lógica heredada del wizard antiguo: al primer login (showOnboarding=true)
 * se aplica viewMode='simple' por defecto y se marca completeOnboarding,
 * para que la lógica downstream no espere a un wizard interactivo.
 */
export function OnboardingWizard({ onStartTour }) {
  const navigate = useNavigate();
  const { showOnboarding, completeOnboarding, setViewMode } = useViewMode();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('vektor_welcome_dismissed') === 'true';
  });

  // Apply default view mode + mark wizard complete on first authenticated render
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    if (showOnboarding) {
      setViewMode('simple');
      completeOnboarding();
    }
  }, [authLoading, isAuthenticated, showOnboarding, setViewMode, completeOnboarding]);

  if (authLoading || !isAuthenticated) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem('vektor_welcome_dismissed', 'true');
    } catch (_) {
      // silencio-ok: localStorage lleno o bloqueado; el aviso se cierra igualmente en esta sesión
    }
    setDismissed(true);
  };

  const handleStartTour = () => {
    handleDismiss();
    if (typeof onStartTour === 'function') {
      onStartTour();
    }
  };

  const handleOpenGuide = () => {
    handleDismiss();
    if (typeof window !== 'undefined') {
      window.open('/docs', '_blank', 'noopener,noreferrer');
    }
  };

  const handleFirstScan = () => {
    handleDismiss();
    navigate('/network-scanner');
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[80] w-[380px] max-w-[calc(100vw-2rem)] pointer-events-auto"
      role="region"
      aria-labelledby="vektor-welcome-title"
    >
      <div className="bg-v4-panel border border-v4-line-2 rounded-xl shadow-card overflow-hidden">
        <div className="px-4 py-3.5 flex items-start gap-3">
          <div className="shrink-0 mt-0.5 h-9 w-9 rounded-md bg-v4-panel-2 border border-v4-line-2 flex items-center justify-center">
            <Shield className="h-4 w-4 text-v4-dim" weight="regular" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-v4-faint">
              PRIMEROS PASOS
            </div>
            <h3
              id="vektor-welcome-title"
              className="mt-0.5 text-[14px] font-semibold text-v4-txt tracking-tight"
            >
              Bienvenido a VEKTOR
            </h3>
            <p className="mt-1 text-[12px] text-v4-dim leading-relaxed">
              Empieza por lanzar tu primer escaneo: es lo que rellena los paneles
              con los datos reales de tu red.
            </p>
            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={handleFirstScan}
                data-testid="welcome-first-scan"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-v4-txt hover:bg-v4-dim text-v4-panel text-[12px] font-semibold transition-colors"
              >
                <MagnifyingGlass className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                Ejecutar primer escaneo
              </button>
              <button
                type="button"
                onClick={handleStartTour}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-v4-line-2 bg-v4-panel-2 hover:bg-v4-line-soft text-v4-txt text-[12px] font-medium transition-colors"
              >
                <PlayCircle className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
                Empezar tour
              </button>
              <button
                type="button"
                onClick={handleOpenGuide}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-v4-line-2 bg-v4-panel-2 hover:bg-v4-line-soft text-v4-txt text-[12px] font-medium transition-colors"
              >
                <BookOpen className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
                Ver guía
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="inline-flex items-center px-2 py-1.5 rounded-md text-v4-dim hover:text-v4-txt hover:bg-v4-panel-2 text-[12px] transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Cerrar bienvenida"
            className="shrink-0 p-1 -mt-1 -mr-1 text-v4-faint hover:text-v4-txt rounded-md hover:bg-v4-panel-2 transition-colors"
          >
            <X className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
